import { FormatHelpers } from "../../helpers/format-helpers";
import { StringHelpers } from "../../helpers/string-helpers";
import { TradeOrderMessageHelpers } from "../../helpers/trade-order-message-helpers";
import { IChartContainer } from "../../models/base/chart-container.interface";
import { ChartSettingsPopupModel } from "../../models/chart-style/chart-settings-popup.model";
import { QuoteType, TradeOrderType } from "../../models/trade/trade.model";
import { IComponent } from "../component.interface";
import { InformationPopupComponent, InformationPopupType } from "../information-popup.component";
import { TradeComponent } from "../trade.component";
import { TradeOrderTypeComponent } from "./trade-order-type.component";

export class BuySellButtonWithCurrentPriceMenuComponent implements IComponent {
    public id = '';

    private element: HTMLElement;
    private base: IChartContainer;
    private tradeComponent: TradeComponent;
    private tradeOrderTypeComponent: TradeOrderTypeComponent;
    private subscribedToTradeComponent = false;
    private yValue = 0;
    private isBuy = false;
    private isMarketOrder = false;
    private closeCallback: Function | null = null;

    constructor(el: HTMLElement, base: IChartContainer) {
        this.id = StringHelpers.newGuid();
        this.element = el;
        this.base = base;
        this.getTradeComponentsIfNotExist();
        this.onClick();
        this.subscribeChartRequirmentData();
    }

    public reset() {
        this.getTradeComponentsIfNotExist();
        if (this.tradeComponent) {
            const model = this.tradeComponent.getTradeModel();
            const isBuy = model.isBuy;
            if (isBuy) {
                this.toBuy();
            } else {
                this.toSell();
            }
    
            this.isMarketOrder = false;
            this.handleMarketOrder();
        }
    }

    public updateValue(yValue: number) {
        this.yValue = yValue;
        this.setLabel();
    }

    public onCloseCallback(cb: Function) {
        this.closeCallback = cb;
    }

    private setLabel() {
        this.getTradeComponentsIfNotExist();

        const val = this.element.findFirst('.val');
        if (this.tradeComponent && this.tradeOrderTypeComponent) {
            if (this.isMarketOrder) {
                val?.addClass('m');
                val?.setInnerHtml(`at Market`);
            } else {
                const priceString = `$${FormatHelpers.numberToStringThousandSeparators(this.yValue, true)}`;
                val?.setInnerHtml(`at ${priceString}`);
                val?.removeClass('m');
            }
        }
    }

    private toBuy() {
        this.isBuy = true;
        this.element.classList.remove('s');
        this.element.classList.add('b');
        this.tradeComponent?.setBuyOrSell(true);
    }

    private toSell() {
        this.isBuy = false;
        this.element.classList.remove('b');
        this.element.classList.add('s');
        this.tradeComponent?.setBuyOrSell(false);
    }

    private validate(): boolean {
        if (!this.yValue) return false;
        if (!this.tradeComponent) return false;
        if (!this.tradeComponent.getQuoteQuantity()) return false;
        
        return true;
    }

    private request() {
        this.getTradeComponentsIfNotExist();

        if (!this.validate()) {
            this.tradeOrderTypeComponent?.focusInput();
            return;
        }

        const tradeModel = this.tradeComponent?.getTradeModel();
        if (tradeModel && this.tradeOrderTypeComponent.value && this.tradeOrderTypeComponent && !isNaN(tradeModel.quoteQuantity)) {
            const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
            let chartSettingsPopupModel = this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel;
            if (!chartSettingsPopupModel) {
                chartSettingsPopupModel = new ChartSettingsPopupModel();
            }

            tradeModel.isBuy = this.isBuy;
            tradeModel.quoteType = this.tradeOrderTypeComponent.value as QuoteType;
            tradeModel.orderType = this.isMarketOrder ? TradeOrderType.Market : tradeModel.orderType;

            if (popup && chartSettingsPopupModel.displayOrderConfirmationPopup) {
                const message = TradeOrderMessageHelpers.getMessage(
                    tradeModel.isBuy,
                    tradeModel.quoteType,
                    this.isMarketOrder ? tradeModel.orderType : null,
                    tradeModel.quoteQuantity,
                    tradeModel.tif,
                    this.base.tiChartSeries.symbol,
                    this.yValue,
                    tradeModel.account);

                popup.show(message.title, message.message, (dontShowMessage: boolean) => {
                    chartSettingsPopupModel.displayOrderConfirmationPopup = !dontShowMessage;
                    this.base.chartService.requestSaveChartSettingsPopupModel(chartSettingsPopupModel);

                    if (this.isMarketOrder) {
                        this.base.chartService.requestManualTrade(tradeModel);
                    } else {
                        this.base.chartService.requestInitiateChartTrade(this.yValue, tradeModel.isBuy, tradeModel.quoteType, this.tradeComponent.getAccountName(), this.tradeComponent.getQuoteQuantity());
                    }

                    this.closeCallback?.();
                }, InformationPopupType.TradeConfirmation, () => {
                    this.closeCallback?.();
                });
            } else {
                if (this.isMarketOrder) {
                    this.base.chartService.requestManualTrade(tradeModel);
                } else {
                    this.base.chartService.requestInitiateChartTrade(this.yValue, tradeModel.isBuy, tradeModel.quoteType, this.tradeComponent.getAccountName(), this.tradeComponent.getQuoteQuantity());
                }

                this.closeCallback?.();
            }
        }
    }

    private getTradeComponentsIfNotExist() {
        if (!this.tradeComponent) {
            this.tradeComponent = this.base.getComponentsByType(TradeComponent)[0] as TradeComponent;
            if (this.tradeComponent) {
                this.tradeComponent.onChange(() => {
                    this.handleMarketOrder();
                });
            }
        }

        if (!this.tradeOrderTypeComponent) {
            this.tradeOrderTypeComponent = new TradeOrderTypeComponent(this.element.findFirst('.trade-order-type'), this.base, this.base.tiChartMenu);
            this.tradeOrderTypeComponent.onChange(() => {
                this.setLabel();
            });

            this.base.components.push(this.tradeOrderTypeComponent);
        }
    }

    private onClick() {
        this.element.findFirst('.button.sell').addEventListener('click', () => {
            if (this.element.containClass('disabled')) {
                return;
            }
        
            this.toSell();
        });
        this.element.findFirst('.button.buy').addEventListener('click', () => {
            if (this.element.containClass('disabled')) {
                return;
            }
        
            this.toBuy();
        });
        this.element.findFirst('.button.submit')?.addEventListener('click', () => {
            if (this.element.containClass('disabled')) {
                return;
            }

            this.request();
        });
        this.element.findFirst('.market')?.addEventListener('click', () => {
            if (this.element.containClass('disabled')) {
                return;
            }

            this.isMarketOrder = !this.isMarketOrder;
            this.handleMarketOrder();
        });
    }

    private handleMarketOrder() {
        const btn = this.element.findFirst('.market');
        if (!btn) return;

        if (this.isMarketOrder) {
            btn?.classList.add('active');
        } else {
            btn?.classList.remove('active');
        }

        this.setLabel();
    }

    private subscribeChartRequirmentData() {
        this.base.chartService.subscribeChartStyleData(() => {
            if (!this.subscribedToTradeComponent) {
                const tradeComponent = this.base.getComponentsByType(TradeComponent)[0] as TradeComponent;
                if (tradeComponent) {
                    this.subscribedToTradeComponent = true;
                    tradeComponent.onChange(() => {
                        this.toggleButton(tradeComponent.getAccountName());
                    });
                    this.toggleButton(tradeComponent.getAccountName());
                }
            }
        });
    }

    private toggleButton(accountName: string) {
        if (!accountName) return;
        this.element.removeClass('disabled');
        this.element.setAttribute('title', 'Buy with ' + accountName);
    }
}